// useGps — cached, NON-PROMPTING GPS hook.
//
// CRITICAL: this hook MUST NOT trigger the browser geolocation prompt.
// Every prompt-firing path goes through `useLocationPrimer().requestLocation`
// so the user always sees the branded priming card first.
//
// Returns { coords, state, requestGps } where:
//   - coords: { lat, lng } | null — read from the same 7-day cache the primer
//             writes to, so distances appear immediately on subsequent visits.
//   - state:  "idle" | "granted" | "denied" | "unsupported"
//   - requestGps(context?): triggers the priming card via the central primer.
//                            Resolves with { ok, lat?, lng?, reason? }.
import { useState } from "react";
import { useLocationPrimer } from "./LocationPrimer";

const LS_KEY = "habano.gps.last";
const LS_DENIED = "habano.location.denied";
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.lat !== "number" || typeof v.lng !== "number") return null;
    if (Date.now() - (v.ts || 0) > MAX_AGE_MS) return null;
    return { lat: v.lat, lng: v.lng };
  } catch { return null; }
}

function readSoftDenied() {
  try { return localStorage.getItem(LS_DENIED) === "1"; } catch { return false; }
}

export function useGps() {
  const primer = useLocationPrimer();
  // Local component-scoped coords slot — gets updated explicitly after a
  // successful requestGps() call. We also seed it from cache on first render
  // so the very first paint shows distances without any effect.
  const [localCoords, setLocalCoords] = useState(() => readCache());
  const [localState, setLocalState] = useState(() => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) return "unsupported";
    if (readCache()) return "granted";
    if (readSoftDenied()) return "denied";
    return "idle";
  });

  // Derive the public state by union'ing local optimistic updates with the
  // primer's observed browser permission state. Pure render — no effect.
  const cached = readCache();
  const coords = localCoords || (primer.permState === "granted" ? cached : null);
  let state = localState;
  if (primer.permState === "granted" && cached) state = "granted";
  else if (primer.permState === "denied") state = "denied";

  const requestGps = async (context = "distance") => {
    const res = await primer.requestLocation(context);
    if (res.ok) {
      setLocalCoords({ lat: res.lat, lng: res.lng });
      setLocalState("granted");
    } else if (res.reason === "denied" || res.reason === "soft-denied") {
      setLocalState("denied");
    }
    return res;
  };

  return { coords, state, requestGps };
}
